import { Button, Form, Input, Space, Tag, Typography } from 'antd';
import type {
  ReducedResearchAgreement,
  ResearchAgreementWithStudentName,
} from '../../Types/interfaces';
import { ResearchAgreementStatus } from '../../Types/interfaces';
import {
  SignResearchAgreementByStudent,
  UpdateResearchAgreement,
} from '../../helper/apiHelper';
import { useTranslation } from 'react-i18next';
import {
  RESEARCH_AGREEMENT_STATUS_COLORS,
  RESEARCH_AGREEMENT_STATUS_LOCALE_KEYS,
} from '../../Constants/constants';
import { CheckCircleFilled, ExclamationCircleFilled } from '@ant-design/icons';
import FileUploader from '../FileUploader';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { usePaperWorkLanguageOfProcess } from '../../hooks/usePaperWorkLanguage';
import { PaperWorkLanguageNotice } from '../PaperWorkLanguageNotice';

const { TextArea } = Input;
const { Text } = Typography;

export interface AgreementTabFormProps {
  item: ResearchAgreementWithStudentName;
  updateAgreements: () => void;
}

export const AgreementTabForm = ({
  item,
  updateAgreements,
}: AgreementTabFormProps): React.ReactElement => {
  const [localForm] = Form.useForm();
  const loggedInUser = useCurrentUser();
  const { t } = useTranslation();
  const paperWorkLanguage = usePaperWorkLanguageOfProcess(
    item.internshipProcessId
  );

  const signatureStatus = (
    agreement: ResearchAgreementWithStudentName
  ): React.ReactElement => {
    if (
      agreement.status === ResearchAgreementStatus.InReview ||
      agreement.status === ResearchAgreementStatus.Accepted
    ) {
      return (
        <Space>
          <CheckCircleFilled style={{ color: 'green' }} />
          <span>{t('researchAgreementComp.alreadySigned')}</span>
        </Space>
      );
    }

    if (
      agreement.status === ResearchAgreementStatus.Active ||
      agreement.status === ResearchAgreementStatus.Rejected
    ) {
      return (
        <Space>
          <ExclamationCircleFilled style={{ color: 'orange' }} />
          <span>{t('researchAgreementComp.pendingYourCompletion')}</span>
        </Space>
      );
    }

    return (
      <Space>
        <CheckCircleFilled style={{ color: 'blue' }} />
        <span>{t('researchAgreementComp.readyToSignByYou')}</span>
      </Space>
    );
  };

  const statusBox = (
    agreement: ResearchAgreementWithStudentName
  ): React.ReactElement => {
    return (
      <div
        style={{
          display: 'flex',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '50px',
          margin: '10px 25px 25px 25px',
          justifyContent: 'center',
        }}
      >
        <div>
          <Text type="secondary">{t('researchAgreementComp.status')}</Text>
          <div>
            <Tag color={RESEARCH_AGREEMENT_STATUS_COLORS[agreement.status]}>
              {t(RESEARCH_AGREEMENT_STATUS_LOCALE_KEYS[agreement.status])}
            </Tag>
          </div>
        </div>

        <div>
          <Text type="secondary">{t('researchAgreementComp.sign')}</Text>
          <div>{signatureStatus(agreement)}</div>
        </div>
      </div>
    );
  };

  const handleSave = async (): Promise<void> => {
    try {
      const values = await localForm.validateFields();

      const researchAgreement: ReducedResearchAgreement = {
        fieldOfResearch: values.fieldOfResearch,
        researchTopic: values.researchTopic,
        descriptionOfResearch: values.description,
      };

      await UpdateResearchAgreement(
        item.internshipProcessId,
        researchAgreement
      );
      await updateAgreements();
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  return (
    <div style={{ marginBottom: '30px' }}>
      {statusBox(item)}
      <PaperWorkLanguageNotice
        language={paperWorkLanguage}
        style={{ marginBottom: 16 }}
      />
      <Typography style={{ color: 'gray' }}>
        {t('researchAgreementComp.fillingInstructions')}
      </Typography>

      <Form layout="vertical" form={localForm}>
        <Form.Item
          label={t('researchAgreementComp.field')}
          name="fieldOfResearch"
          initialValue={item.fieldOfResearch}
          rules={[
            {
              required: true,
              message: t('researchAgreementComp.requiredField'),
            },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label={t('researchAgreementComp.researchTopic')}
          name="researchTopic"
          initialValue={item.researchTopic}
          rules={[
            {
              required: true,
              message: t('researchAgreementComp.requiredField'),
            },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label={t('researchAgreementComp.researchDesc')}
          name="description"
          initialValue={item.descriptionOfResearch}
          rules={[
            {
              required: true,
              message: t('researchAgreementComp.requiredField'),
            },
          ]}
        >
          <TextArea rows={4} />
        </Form.Item>

        <Form.Item>
          <Button type="primary" onClick={handleSave}>
            {t('researchAgreementComp.generateDoc')}
          </Button>
        </Form.Item>
      </Form>

      {item.status === ResearchAgreementStatus.Generated && (
        <>
          <Typography style={{ color: 'gray' }}>
            {t('researchAgreementComp.uploadingInstrunctions')}
          </Typography>
          <FileUploader
            role={loggedInUser.roles}
            apiUrl={'research-agreement/upload?role=1'}
            documentUrl={item.researchAgreementDocumentUrl}
            documentSigningStatus={ResearchAgreementStatus[item.status]}
            isSignatureDisabled={
              item.status !== ResearchAgreementStatus.Generated
            }
            alreadySigned={false}
            onFileUpload={async () => {
              await SignResearchAgreementByStudent(
                loggedInUser.id,
                item.internshipProcessId
              );
              await updateAgreements();
            }}
          />
        </>
      )}
    </div>
  );
};
